import { ref, watch } from "vue";

import type {
  AutomationId,
  AutomationRunId,
  Controller,
  OrgId,
} from "@ctrl/controller-rpc";

import { releaseBestEffort } from "@/ref";

export type RunSummary = {
  id: AutomationRunId;
  started_at: Date;
  status: "completed" | "failed" | "pending_user_input" | "pending_resume";
};

const isPending = (status: RunSummary["status"]): boolean =>
  status === "pending_user_input" || status === "pending_resume";

// Call useAutomationRuns() synchronously in setup() before any await, passing
// the controller value resolved from inject.controller() at the call site.
// Register the returned release() for unmount (e.g. onBeforeUnmount(release))
// before any await, then call the returned init() to perform async
// initialization.
export function useAutomationRuns(
  controller: Controller,
  organizationId: OrgId,
  automationId: AutomationId
) {
  let released = false;

  type RunIdsRef = Awaited<ReturnType<typeof controller.ai.automation.run.all>>;

  const sortedRuns = ref<RunSummary[]>([]);
  const startedAtCache = new Map<AutomationRunId, Date>();
  let runIdsRef: RunIdsRef;
  let unwatch: ReturnType<typeof watch> | undefined;

  const syncIds = async (v: RunIdsRef["value"]) => {
    if (!v) return;
    const buckets: Array<{
      ids: AutomationRunId[];
      status: RunSummary["status"];
    }> = [
      {
        ids: v.completed ?? [],
        status: "completed",
      },
      { ids: v.failed ?? [], status: "failed" },
      {
        ids: v.pending_user_input ?? [],
        status: "pending_user_input",
      },
      {
        ids: v.pending_resume ?? [],
        status: "pending_resume",
      },
    ];
    const runs: RunSummary[] = [];
    for (const { ids, status } of buckets) {
      for (const id of ids) {
        if (!startedAtCache.has(id)) {
          // started_at is immutable so we snapshot and release immediately.
          const run = (
            await controller.ai.automation.run.get(organizationId, id)
          ).release();
          if (released) return;
          if (run) startedAtCache.set(id, run.started_at);
        }
        const started_at = startedAtCache.get(id);
        if (started_at) runs.push({ id, started_at, status });
      }
    }
    // Surface runs awaiting input first, then most-recent within each group.
    runs.sort((a, b) => {
      const aPending = isPending(a.status);
      const bPending = isPending(b.status);
      if (aPending !== bPending) return aPending ? -1 : 1;
      return b.started_at.getTime() - a.started_at.getTime();
    });
    if (!released) sortedRuns.value = runs;
  };

  // Idempotent; the calling component registers this for unmount.
  const release = () => {
    if (released) return;
    released = true;
    unwatch?.();
    if (runIdsRef) releaseBestEffort(runIdsRef);
  };

  const deleteRun = async (runId: AutomationRunId) => {
    await controller.ai.automation.run.delete(organizationId, runId);
  };

  const init = async () => {
    const acquired = await controller.ai.automation.run.all(
      organizationId,
      automationId
    );
    // release() may have run while we were suspended: it saw runIdsRef still
    // unset, so the ref that just landed is ours to release.
    if (released) {
      releaseBestEffort(acquired);
      return;
    }
    runIdsRef = acquired;
    await syncIds(runIdsRef.value);
    if (released) return;
    unwatch = watch(
      () => runIdsRef.value,
      (newVal) => syncIds(newVal)
    );
  };

  return { init, sortedRuns, deleteRun, release };
}
